import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, model, OnInit, Optional, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { RxState } from '@rx-angular/state';
import { CrmObjectInterface } from '@vendasta/crm';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import {
  FieldConfigInterface,
  LoadingStatus,
  ObjectType,
  SavingStatus,
  SNACKBAR_DURATION,
  TranslationModule,
  CrmModelDrivenFormComponent,
  CrmObjectDependencies,
  CrmObjectInjectionToken,
} from '@galaxy/crm/static';
import { CrmDependencies, CrmInjectionToken, CrmFormService } from '@galaxy/crm/static';

@Component({
  selector: 'crm-contact-profile-content',
  templateUrl: './profile-content.component.html',
  styleUrls: ['./profile-content.component.scss'],
  providers: [RxState],
  imports: [CommonModule, TranslateModule, TranslationModule, CrmModelDrivenFormComponent],
})
export class CrmContactProfileContentComponent implements OnInit {
  @Input({ required: true }) objectType!: ObjectType;
  @Input() set crmObject(crmObject: CrmObjectInterface) {
    this.state.set({ crmObject: crmObject });
  }
  get crmObject(): CrmObjectInterface {
    return this.state.get('crmObject');
  }
  @Input() fieldIdsToHide: string[] = [];
  @Output() objectChanged = new EventEmitter();

  loadingStatus = model<LoadingStatus>(LoadingStatus.LOADING);
  SavingStatus = SavingStatus;
  savingStatus$$ = new BehaviorSubject<SavingStatus>(SavingStatus.NOT_SAVING);

  fieldConfig$: Observable<FieldConfigInterface> = of({});
  crmObjectId$ = this.state.select('crmObject').pipe(
    filter((crmObject) => !!crmObject?.crmObjectId),
    map((crmObject) => crmObject.crmObjectId),
  );

  constructor(
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly state: RxState<ComponentState>,
    private readonly crmFormService: CrmFormService,
    private readonly snackService: SnackbarService,
    @Optional()
    @Inject(CrmObjectInjectionToken)
    private readonly crmObjectDependencies: CrmObjectDependencies,
  ) {
    state.set({
      crmObject: {},
      email: '',
      phoneNumber: '',
      tags: [],
    });
  }

  ngOnInit(): void {
    const fieldSchemas$ = this.state.select('crmObject').pipe(
      filter((crmObject) => !!crmObject?.crmObjectId),
      switchMap(({ crmObjectId }) =>
        this.crmFormService.listAllFormFieldsInitializedWithCrmObject$(this.objectType, crmObjectId || ''),
      ),
    );
    const fieldGroups$ = this.crmFormService.listAllObjectFormFieldGroups$(
      this.objectType,
      this.crmObjectDependencies?.objectSubtype$,
    );
    const fieldConfig$ = combineLatest([fieldSchemas$, fieldGroups$]).pipe(
      tap(([fieldSchemas, fieldGroups]) => {
        const empty = fieldSchemas.length === 0 || fieldGroups.length === 0;
        this.loadingStatus.set(empty ? LoadingStatus.EMPTY : LoadingStatus.OK);
      }),
      map(([fieldSchemas, fieldGroups]) => {
        return {
          fieldSchemas: fieldSchemas,
          fieldGroups: fieldGroups,
        };
      }),
      catchError((error) => {
        this.loadingStatus.set(LoadingStatus.ERROR);
        this.errorSnackbar(error, 'CONTACT.EDIT_CONTACT_PAGE.LOAD.ERROR');
        return of({} as FieldConfigInterface);
      }),
      shareReplay(1),
    );
    this.fieldConfig$ = fieldConfig$.pipe(
      // remove first name and last name, company name since they are available at the top of the profile
      // TODO: remove this when we can configure layouts
      map((fieldConfig) => {
        return this.hideFields(fieldConfig);
      }),
    );
  }

  private hideFields(fieldConfig: FieldConfigInterface): FieldConfigInterface {
    const fieldGroups = (fieldConfig.fieldGroups || []).map(({ fieldIds, description }) => ({
      description,
      fieldIds: fieldIds.filter((id) => {
        return this.fieldIdsToHide.indexOf(id) === -1;
      }),
    }));
    return {
      ...fieldConfig,
      fieldGroups,
    };
  }

  private errorSnackbar(error: any | null, defaultMessage: string) {
    let message: string | null = null;
    if (error && error.message) {
      message = error.message;
    }
    this.snackService.openErrorSnack(message ?? defaultMessage, {
      duration: SNACKBAR_DURATION,
    });
  }
}

interface ComponentState {
  crmObject: CrmObjectInterface;
  fullName: string;
  email: string;
  phoneNumber: string;
  tags: string[];
}
